import { useGetIntercomJWT } from '@cobliteam/hooks/src/useEntity/useSession';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import useAuthStore from '@cobliteam/hooks/src/useSession/store/useAuthStore';
import Intercom from '@intercom/messenger-js-sdk';
import { useEffect, useEffectEvent } from 'react';
import { INTERCOM_HORIZONTAL_PADDING, INTERCOM_VERTICAL_PADDING } from '../useChatWidget/intercomPadding';

const INTERCOM_APP_ID = 'z9kpl4jw';

// Intercom() só age na primeira chamada da página; o boot e o listener de onHide são globais.
let hasBooted = false;
// Depois do primeiro carregamento do SDK, um novo boot (após shutdown) passa pelo próprio widget.
let hasLoadedSdk = false;

/** Encerra a sessão do Intercom (logout): o próximo usuário da mesma página não herda o messenger. */
export const shutdownIntercom = () => {
  if (!hasBooted) return;
  window.Intercom?.('shutdown');
  hasBooted = false;
};

/** Faz o boot do Intercom uma vez por página, já com o JWT de verificação de identidade. */
export default function useIntercom({ onClose, enabled = true }: { onClose: () => void; enabled?: boolean }) {
  const session = useAuthStore((s) => s.session);
  const { blipChat } = useFeatureFlags();
  const shouldLoad = Boolean(enabled && blipChat && session);
  const { data: intercomJwt, isPending: isJwtPending } = useGetIntercomJWT({ enabled: shouldLoad });
  const handleHide = useEffectEvent(() => onClose());

  useEffect(() => {
    // Com erro no JWT o boot segue sem ele, para o usuário ainda conseguir abrir o messenger.
    if (!(shouldLoad && session) || hasBooted || isJwtPending) return;

    const settings = {
      app_id: INTERCOM_APP_ID,
      name: session.user.name,
      email: session.user.email,
      hide_default_launcher: true,
      horizontal_padding: INTERCOM_HORIZONTAL_PADDING,
      vertical_padding: INTERCOM_VERTICAL_PADDING,
      ...(intercomJwt && { intercom_user_jwt: intercomJwt }),
      ...(session.user.creationDate && { created_at: Math.floor(session.user.creationDate / 1000) }),
      company: {
        company_id: session.fleet.id,
        name: session.fleet.name,
      },
    };
    if (hasLoadedSdk && window.Intercom) window.Intercom('boot', settings);
    else Intercom(settings);
    hasLoadedSdk = true;
    hasBooted = true;

    window.Intercom?.('onHide', () => handleHide());
  }, [shouldLoad, session, isJwtPending, intercomJwt]);
}

export const resetIntercomBootForTests = () => {
  hasBooted = false;
  hasLoadedSdk = false;
};
